import { Star, User, GitBranch, ListChecks } from 'lucide-react';
import { Badge } from '../../ui';
import { stageTone, isFollowing } from '../../features/workstreams/workstreamModel';
import { stageLabel } from '../../features/workstreams/workstreamConfig';
import type { WorkstreamSummary } from '../../features/workstreams/types';

export interface WorkstreamCardProps {
  workstream: WorkstreamSummary;
  /** Current user's alias — drives the follow-state affordance. */
  currentAlias: string;
  /** Hide the stage badge (used when the board is already grouped by stage). */
  hideStage?: boolean;
  /** Hide the engineer line (used when the board is already grouped by engineer). */
  hideEngineer?: boolean;
  onOpen: (w: WorkstreamSummary) => void;
  onToggleFollow: (w: WorkstreamSummary) => void;
}

/**
 * A single workstream card for the "what is everyone working on?" board. Shows
 * the title, stage, owner, affected systems, story/follower counts, and a
 * follow toggle. Opening the card navigates to the detail view.
 */
export function WorkstreamCard({
  workstream,
  currentAlias,
  hideStage,
  hideEngineer,
  onOpen,
  onToggleFollow,
}: WorkstreamCardProps) {
  const following = isFollowing(workstream.followers, currentAlias);
  const followerCount = workstream.followers.length;

  return (
    <div className="group rounded-xl border border-surface-border bg-surface-1 p-3 shadow-sm transition-colors hover:border-zinc-600">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => onOpen(workstream)}
          className="flex-1 text-left text-sm font-medium text-zinc-100 hover:text-white"
        >
          {workstream.title || 'Untitled workstream'}
        </button>
        <button
          type="button"
          onClick={() => onToggleFollow(workstream)}
          aria-pressed={following}
          aria-label={following ? 'Unfollow workstream' : 'Follow workstream'}
          title={following ? 'Following' : 'Follow'}
          className={[
            'inline-flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1 text-xs transition-colors',
            following
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
              : 'border-surface-border text-zinc-400 hover:border-zinc-600 hover:text-zinc-200',
          ].join(' ')}
        >
          <Star
            className={`h-3.5 w-3.5 ${following ? 'fill-amber-300' : ''}`}
            aria-hidden="true"
          />
          {followerCount > 0 && (
            <span className="tabular-nums">{followerCount}</span>
          )}
        </button>
      </div>

      {!hideStage && (
        <div className="mt-2">
          <Badge tone={stageTone(workstream.currentStage)} dot>
            {stageLabel(workstream.currentStage)}
          </Badge>
        </div>
      )}

      {workstream.systemsAffected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {workstream.systemsAffected.slice(0, 4).map((s) => (
            <Badge key={s} tone="neutral">
              {s}
            </Badge>
          ))}
          {workstream.systemsAffected.length > 4 && (
            <Badge tone="neutral">
              +{workstream.systemsAffected.length - 4}
            </Badge>
          )}
        </div>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
        {!hideEngineer && workstream.engineer && (
          <span className="inline-flex items-center gap-1">
            <User className="h-3 w-3" aria-hidden="true" />
            {workstream.engineer}
          </span>
        )}
        {workstream.sprintStoryCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <ListChecks className="h-3 w-3" aria-hidden="true" />
            {workstream.sprintStoryCount}{' '}
            {workstream.sprintStoryCount === 1 ? 'story' : 'stories'}
          </span>
        )}
        {workstream.goalId && (
          <span className="inline-flex items-center gap-1">
            <GitBranch className="h-3 w-3" aria-hidden="true" />
            linked goal
          </span>
        )}
      </div>
    </div>
  );
}

export default WorkstreamCard;
